import React from 'react';
import PropTypes from 'prop-types';
import worldData from './world.geo.json'; // eslint-disable-line
import parsedWorldData from './parsed-world2.geo.json'; // eslint-disable-line
import countryNames from './world-country-names.json'; // eslint-disable-line

import { roundNumber } from 'utils/general';
import MapLegend from 'components/country-profiles/MapLegend';
import { LEGEND_VALUES } from 'constants/countryProfile';

class Globe extends React.Component {
  constructor(props) {
    super();
    this.props = props;
    this.sens = 0.5;
    this.timer = null;
    this.hasNewCountry = false;
    this.worldData = worldData;
    this.parsedWorldData = parsedWorldData;
    this.countryNames = countryNames;
  }

  componentDidMount() {
    this.init();
    this.setListeners();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedCountry !== this.props.selectedCountry) {
      this.hasNewCountry = true;
    }
  }

  componentDidUpdate() {
    if (this.hasNewCountry) {
      this.setSelectedCountry();
      this.centerGlobe();
      this.hasNewCountry = false;
    }
  }

  componentWillUnmount() {
    this.unsetListeners();
  }

  setListeners() {
    this.onPageResize = () => this.debounceDraw();
    window.addEventListener('resize', this.onPageResize);
  }

  setInteractivity() {
    const that = this;
    // Drag event for the whole globe.
    this.svg.call(d3.behavior.drag()
      .origin(() => {
        const r = this.projection.rotate();
        return { x: r[0] / this.sens, y: -r[1] / this.sens };
      })
      .on('drag', () => {
        const rotate = this.projection.rotate();
        this.projection.rotate([d3.event.x * this.sens, -d3.event.y * this.sens, rotate[2]]);
        this.svg.selectAll('path.land').attr('d', this.path);
        this.svg.selectAll('path.graticule').attr('d', this.path);
      }));

    // Hover event only for "land" paths
    this.svg.selectAll('path.land').on('mouseover', function onLandMouseOver(d) {
      function getDualSectorInfo(score, yearOfData) {
        return ` <div class="sector-info-wrapper">
                    <div class="sector-info">
                      <div class="score">Score
                        <span class="data">${score.mining && score.mining[`score${yearOfData}`] ? roundNumber(score.mining[`score${yearOfData}`]) : '–'}</span>
                        <span class="max"> /100</span>
                      </div>
                      <span class="legend sector-legend">Mining</span>
                    </div>
                    <div class="sector-info" >
                    <div class="score">Score
                        <span class="data">${score['oil-gas'] && score['oil-gas'][`score${yearOfData}`] ? roundNumber(score['oil-gas'][`score${yearOfData}`]) : '–'}</span>
                        <span class="max"> /100</span>
                    </div>
                    <span class="legend sector-legend">Oil & Gas</span>
                  </div>
                  <span class="legend bold year-legend">${yearOfData}</span>
                  </div>`;
      }
      function getSingleSectorInfo(sector, score, yearOfData) {
        return ` <div class="sector-info-wrapper">
        <div class="sector-info">
          <div class="score">Score
            <span class="data">${score && score[`score${yearOfData}`] ? roundNumber(score[`score${yearOfData}`]) : '–'}</span>
            <span class="max">/100</span>
          </div>
          <span class="legend sector-legend">${sector}</span>
        </div>
           <span class="legend bold year-legend">${yearOfData}</span>
        </div>`;
      }
      const { iso } = d.properties;
      if (that.countriesTable[iso] &&
        that.countriesTable[iso].score) {
        const hasDualSector = that.countriesTable[iso].score &&
          (that.countriesTable[iso].score.mining || that.countriesTable[iso].score['oil-gas']);

        let html = '';

        if (hasDualSector) {
          const { score, name, allYears } = that.countriesTable[iso];
          html = `<div class="c-hoverinfo -dual-sector">
                <div class="header">
                  <p class="title -center">${name}</p>
                </div>
                <div class="content">
                  ${allYears.map(el => getDualSectorInfo(score, el)).join('')}
                </div>
              </div>`;
        } else {
          const { name, score, sector, allYears } = that.countriesTable[iso];

          html = `<div class="c-hoverinfo">
              <div class="header">
                <p class="title -center">${name}</p>
              </div>
              <div class="content">
              ${allYears.map(el => getSingleSectorInfo(sector, score, el)).join('')}
              </div>
            </div>`;
        }

        d3.select(this).classed('-hover', true);
        that.countryTooltip
          .html(html)
          .style('left', `${(d3.event.pageX + 10)}px`)
          .style('top', `${(d3.event.pageY)}px`)
          .style('display', 'block')
          .style('opacity', 1);
      }
    })
    .on('mouseout', function onLandMouseOut() {
      d3.select(this).classed('-hover', false);
      that.countryTooltip.style('opacity', 0)
        .style('display', 'none');
    })
    .on('mousemove', function onLandMouseMove() {
      const coordinates = d3.mouse(this);
      that.countryTooltip
        .style('left', `${(coordinates[0] + 10)}px`)
        .style('top', `${(coordinates[1])}px`);
    })
    .on('click', (d) => {
      const { iso } = d.properties;
      if (this.countriesTable[iso] &&
        this.countriesTable[iso].score) {
        this.props.handleSelection({
          iso: this.countriesTable[iso].iso,
          score: this.countriesTable[iso].score || null
        });
      }
    });
  }

  setSelectedCountry() {
    this.focusedCountry = this.getCountry(this.props.selectedCountry);
  }

  getCountry(selectedIso) {
    if (selectedIso) {
      for (let i = 0, l = this.parsedCountries.length; i < l; i++) {
        if (this.parsedCountries[i].properties.iso === selectedIso) {
          return this.parsedCountries[i];
        }
      }
    }
    return false;
  }

  setLevel(scoreObj, yearOfData) {
    let level = '';
    const score = scoreObj && scoreObj[`score${yearOfData}`] ? scoreObj[`score${yearOfData}`] : null;
    switch (true) {
      case (score < 30): level = '-failing'; break;
      case (score < 45): level = '-weak'; break;
      case (score < 60): level = '-partial'; break;
      case (score < 75): level = '-satisfactory'; break;
      case (score <= 100): level = '-good'; break;
      default: level = '-no-data';
    }

    return level;
  }

  animateGlobe(svg, projection, path, p) {
    // There is an issue when handling three globes at the same time.
    // The scope gets confused and there is only animation in the last rendered item.
    // TODO: Check this poltergeist.
    // d3.transition()
    //   .duration(2500)
    //   .tween('rotate', () => {
    //     console.log('out of return', svg);
    //     const r = d3.interpolate(projection.rotate(), [-p[0], -p[1]]);
    //     return function(t) {
    //       console.log('inside return', svg)
    //       projection.rotate(r(t));
    //       svg.selectAll('path.land').attr('d', path);
    //     }.bind(this);
    // });

    // Center without animation
    projection.rotate([-p[0], -p[1]]);

    this.svg
      .selectAll('path.land')
      .attr('d', path)
      .classed('-selected', (d) => ((this.focusedCountry && d.properties.iso === this.focusedCountry.properties.iso) || false));
  }


  centerGlobe() {
    const p = d3.geo.centroid(this.focusedCountry);
    this.animateGlobe(this.svg, this.projection, this.path, p);
  }

  unsetListeners() {
    window.removeEventListener('resize', this.onPageResize);
  }

  debounceDraw() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.draw();
    }, 300);
  }

  init() {
    // Parse countries name
    this.countriesTable = {};
    if (this.props.countries) {
      Object.values(this.props.countries).forEach((country) => {
        const { iso, name, rank, score, sector, yearOfData, classSuffix, latestYear, allYears } = country;
        this.countriesTable[iso] = {
          name,
          iso,
          rank: rank || null,
          score: score || null,
          allYears: allYears || [],
          sector: sector || null,
          yearOfData: yearOfData || null,
          latestYear: latestYear || null,
          classSuffix: classSuffix || ''
        };
      });
    }

    // Get topojson countries
    this.countries = topojson.feature(this.worldData, this.worldData.objects.countries).features;
    this.parsedCountries = topojson.feature(this.parsedWorldData, this.parsedWorldData.objects.countries).features;

    // Select and cache svg
    this.svg = d3.select(this.globe);
    this.countryTooltip = d3.select(this.globe).append('div').attr('class', 'tooltip');
    this.draw();
  }

  draw() {
    // Size
    const width = this.globe.offsetWidth * (this.props.includeLegend ? 0.80 : 1);
    const height = width;

    // Setting projection
    this.projection = d3.geo.orthographic()
      .scale((height / 2) - 5)
      .rotate([0, 0])
      .translate([width / 2, height / 2])
      .clipAngle(90);

    this.path = d3.geo.path()
      .projection(this.projection);

    const graticule = d3.geo.graticule();

    // SVG container
    this.svg.select('svg').remove();
    const svg = this.svg.append('svg')
      .attr('width', width)
      .attr('height', height);

    const basemap = svg.append('g');
    const layer = svg.append('g');

    // Adding water
    basemap.append('path')
      .datum({ type: 'Sphere' })
      .attr('class', 'water')
      .attr('d', this.path);

    basemap.append('path')
      .datum(graticule)
      .attr('class', 'graticule')
      .attr('d', this.path);

    // Set basemap
    basemap.selectAll('path.base')
      .data(this.parsedCountries)
      .enter()
      .append('path')
      .attr('class', 'land')
      .attr('d', this.path);

    layer.selectAll('path.land')
      .data(this.parsedCountries)
      .enter()
      .append('path')
      .attr('class', (d) => {
        const { iso } = d.properties;
        let level = '';
        let classSuffix = '';
        if (this.countriesTable[iso]) {
          if (this.countriesTable[iso].score && this.countriesTable[iso].yearOfData) {
            classSuffix = this.countriesTable[iso].classSuffix;
            level = this.setLevel(this.countriesTable[iso].score, this.countriesTable[iso].yearOfData);
            level = level + classSuffix;
          }

          if (this.countriesTable[iso].score &&
            this.countriesTable[iso].yearOfData &&
             (this.countriesTable[iso].score.mining || this.countriesTable[iso].score['oil-gas'])) {
            const levelClasses = Object.values(this.countriesTable[iso].score).map(s => this.setLevel(s, this.countriesTable[iso].yearOfData));
            level = levelClasses.join('') + classSuffix;
          }
          const { yearOfData, latestYear } = this.countriesTable[iso];
          if (yearOfData && latestYear !== yearOfData) {
            level = `-failing${classSuffix}`;// manual overriding as per last year
          }
        }

        return `land ${level} ${level ? '-covered' : ''}`;
      })
      .attr('d', this.path);

    // Set mouse events
    if (this.props.interactivity) this.setInteractivity();

    // Set selected country and rotate
    if (this.props.selectedCountry) {
      this.setSelectedCountry();
      this.centerGlobe();
    }
  }

  render() {
    const isoKeys = this.props.countries ? Object.keys(this.props.countries) : [];
    const latestYear = isoKeys.length
    ? this.props.countries[isoKeys[0]].latestYear
    : undefined;
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div className={`c-globe ${this.props.theme} ${!this.props.interactivity && '-inactive'}`} ref={(globe) => { this.globe = globe; }}>
        </div>
        {this.props.includeLegend && <div className="row justify-content-between flex-wrap">
          <MapLegend titleYear={latestYear} data={LEGEND_VALUES} parentClass="-globe-legend" />
        </div>}
      </div>

    );
  }
}

Globe.propTypes = {
  countries: PropTypes.object,
  theme: PropTypes.string,
  interactivity: PropTypes.bool,
  selectedCountry: PropTypes.string,
  loading: PropTypes.bool,
  handleSelection: PropTypes.func,
  includeLegend: PropTypes.bool
};

export default Globe;



// WEBPACK FOOTER //
// ./app/components/common/globe/index.jsx